import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Button from "@mui/material/Button";
import Slider from "@mui/material/Slider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Stage, Layer, Image as KonvaImage, Group, Rect, Circle, Text, Line, Arrow as KonvaArrow } from "react-konva";

const clamp01 = (n) => Math.max(0, Math.min(1, n));
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

function normalizeXY(v) {
  if (typeof v !== "number" || Number.isNaN(v)) return 0;
  if (v > 1) return clamp01(v / 100);
  return clamp01(v);
}

function normalizeAnnotation(a = {}) {
  const type =
    a.type === "circle" || a.type === "note" || a.type === "text" || a.type === "arrow" || a.type === "rect" || a.type === "path"
      ? a.type
      : "note";
  const base = {
    id: a.id || `ann_${Math.random().toString(16).slice(2)}_${Date.now()}`,
    type,
    x: normalizeXY(a.x),
    y: normalizeXY(a.y),
    color: a.color || "#0A84FF",
    opacity: typeof a.opacity === "number" ? clamp(a.opacity, 0.2, 1) : 1,
    label: typeof a.label === "string" ? a.label : "",
    note: typeof a.note === "string" ? a.note : "",
    labelMode: a.labelMode === "none" || a.labelMode === "number" || a.labelMode === "text" ? a.labelMode : undefined,
    zIndex: typeof a.zIndex === "number" ? a.zIndex : 0,
  };

  if (type === "circle") {
    return {
      ...base,
      r: typeof a.r === "number" ? clamp(a.r, 0.005, 0.35) : 0.04,
      strokeWidth: typeof a.strokeWidth === "number" ? clamp(a.strokeWidth, 0.001, 0.03) : 0.004,
      fillOpacity: typeof a.fillOpacity === "number" ? clamp(a.fillOpacity, 0, 0.6) : 0,
    };
  }

  if (type === "text") {
    return {
      ...base,
      labelMode: "text",
      fontSize: typeof a.fontSize === "number" ? clamp(a.fontSize, 0.01, 0.08) : 0.026,
      bgOpacity: typeof a.bgOpacity === "number" ? clamp(a.bgOpacity, 0, 0.95) : 0.35,
      textColor: typeof a.textColor === "string" ? a.textColor : "#FFFFFF",
      w: typeof a.w === "number" ? clamp(a.w, 0.10, 0.95) : 0.28,
      h: typeof a.h === "number" ? clamp(a.h, 0.06, 0.70) : 0.10,
      radius: typeof a.radius === "number" ? clamp(a.radius, 0, 0.06) : 0.012,
      padding: typeof a.padding === "number" ? clamp(a.padding, 0.002, 0.05) : 0.012,
    };
  }

  if (type === "rect") {
    return {
      ...base,
      w: typeof a.w === "number" ? clamp(a.w, 0.02, 0.95) : 0.10,
      h: typeof a.h === "number" ? clamp(a.h, 0.02, 0.95) : 0.10,
      strokeWidth: typeof a.strokeWidth === "number" ? clamp(a.strokeWidth, 0.001, 0.03) : 0.004,
      fillOpacity: typeof a.fillOpacity === "number" ? clamp(a.fillOpacity, 0, 0.6) : 0,
      radius: typeof a.radius === "number" ? clamp(a.radius, 0, 0.06) : 0.008,
    };
  }

  if (type === "path") {
    const pts = Array.isArray(a.points) ? a.points : [];
    const cleanPts = pts
      .map((n) => (typeof n === "number" && Number.isFinite(n) ? n : null))
      .filter((n) => n != null);
    const evenPts = cleanPts.length % 2 === 0 ? cleanPts : cleanPts.slice(0, cleanPts.length - 1);
    const clampedPts = evenPts.map((n) => clamp01(n));
    return {
      ...base,
      points: clampedPts.length >= 4 ? clampedPts : [base.x, base.y, clamp01(base.x + 0.02), clamp01(base.y + 0.02)],
      strokeWidth: typeof a.strokeWidth === "number" ? clamp(a.strokeWidth, 0.0008, 0.03) : 0.0035,
      tension: typeof a.tension === "number" ? clamp(a.tension, 0, 1) : 0.4,
      lineCap: a.lineCap === "round" || a.lineCap === "butt" || a.lineCap === "square" ? a.lineCap : "round",
      lineJoin: a.lineJoin === "round" || a.lineJoin === "bevel" || a.lineJoin === "miter" ? a.lineJoin : "round",
    };
  }

  if (type === "arrow") {
    const x1 = typeof a.x1 === "number" ? clamp01(a.x1) : clamp01(base.x - 0.06);
    const y1 = typeof a.y1 === "number" ? clamp01(a.y1) : clamp01(base.y - 0.02);
    const x2 = typeof a.x2 === "number" ? clamp01(a.x2) : clamp01(base.x + 0.06);
    const y2 = typeof a.y2 === "number" ? clamp01(a.y2) : clamp01(base.y + 0.02);
    return {
      ...base,
      x: clamp01((x1 + x2) / 2),
      y: clamp01((y1 + y2) / 2),
      x1,
      y1,
      x2,
      y2,
      strokeWidth: typeof a.strokeWidth === "number" ? clamp(a.strokeWidth, 0.001, 0.03) : 0.004,
      headSize: typeof a.headSize === "number" ? clamp(a.headSize, 0.006, 0.08) : 0.02,
    };
  }

  // note
  return {
    ...base,
    size: typeof a.size === "number" ? clamp(a.size, 0.6, 2.5) : 1,
    labelMode: base.labelMode ?? "number",
  };
}

function byZ(a, b) {
  return (a?.zIndex || 0) - (b?.zIndex || 0);
}

function useElementSize(enabled) {
  const [node, setNode] = useState(null);
  const [size, setSize] = useState({ w: 1, h: 1 });

  const ref = useCallback((el) => {
    setNode(el || null);
  }, []);

  useLayoutEffect(() => {
    if (!enabled || !node) return undefined;
    let cancelled = false;

    const measure = () => {
      if (cancelled) return;
      let el = node;
      let rect = el.getBoundingClientRect();
      for (let i = 0; i < 6 && (rect.width <= 2 || rect.height <= 2); i += 1) {
        el = el.parentElement || el;
        if (!el || el === node.ownerDocument?.body) break;
        rect = el.getBoundingClientRect();
      }
      const w = rect.width || el.clientWidth || el.offsetWidth || 1;
      const h = rect.height || el.clientHeight || el.offsetHeight || 1;
      setSize({ w, h });
      return { w, h };
    };

    const start = Date.now();
    const tick = () => {
      const { w, h } = measure() || { w: 1, h: 1 };
      if (cancelled) return;
      if ((w <= 2 || h <= 2) && Date.now() - start < 1200) window.setTimeout(tick, 50);
    };

    measure();
    const raf = window.requestAnimationFrame ? window.requestAnimationFrame(tick) : null;
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(node);
    } else {
      window.addEventListener("resize", measure);
    }
    return () => {
      cancelled = true;
      if (raf != null && window.cancelAnimationFrame) window.cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", measure);
    };
  }, [enabled, node]);

  return [ref, size];
}

export default function AnnotatedImageViewerDialog({
  open,
  title,
  imageUrl,
  annotations,
  onClose,
  onPrev,
  onNext,
  canPrev = false,
  canNext = false,
  positionLabel = "",
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 }); // px in canvas space
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const touchRef = useRef({ mode: "none", startDist: 0, startZoom: 1, startPan: { x: 0, y: 0 }, startCenter: { x: 0, y: 0 } });
  const [imgObj, setImgObj] = useState(null);
  const [imgNatural, setImgNatural] = useState({ w: 1, h: 1 });
  const [imgError, setImgError] = useState(null);
  const [canvasRef, canvasSize] = useElementSize(open);

  useEffect(() => {
    if (!open) return;
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [open]);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  useEffect(() => {
    if (!open) return;
    if (!imageUrl) {
      setImgObj(null);
      setImgNatural({ w: 1, h: 1 });
      setImgError("No image URL");
      return;
    }
    setImgError(null);
    const img = new window.Image();
    img.onload = () => {
      setImgObj(img);
      setImgNatural({ w: img.naturalWidth || img.width || 1, h: img.naturalHeight || img.height || 1 });
    };
    img.onerror = () => {
      setImgObj(null);
      setImgNatural({ w: 1, h: 1 });
      setImgError("Failed to load image");
    };
    img.src = imageUrl;
  }, [open, imageUrl]);

  const anns = useMemo(() => (Array.isArray(annotations) ? annotations : []).map(normalizeAnnotation).sort(byZ), [annotations]);

  const fitBase = useMemo(() => {
    const maxW = canvasSize.w || 1;
    const maxH = canvasSize.h || 1;
    const w = imgNatural.w || 1;
    const h = imgNatural.h || 1;
    const s = Math.min(maxW / w, maxH / h);
    const scale = Math.max(0.05, s);
    const drawW = w * scale;
    const drawH = h * scale;
    return { scale, x: (maxW - drawW) / 2, y: (maxH - drawH) / 2 };
  }, [canvasSize.w, canvasSize.h, imgNatural.w, imgNatural.h]);

  const totalScale = useMemo(() => fitBase.scale * zoom, [fitBase.scale, zoom]);

  // Allow zooming out beyond "fit" so labels/marks near edges are visible.
  const clampZoom = useCallback((z) => clamp(z, 0.15, 16), []);

  const zoomAtCanvasPoint = useCallback(
    (nextZoom, point) => {
      const z = clampZoom(nextZoom);
      const baseX = fitBase.x;
      const baseY = fitBase.y;
      const baseS = fitBase.scale || 1;
      const currZ = zoomRef.current || 1;
      const currPan = panRef.current || { x: 0, y: 0 };

      const currTotal = baseS * currZ;
      const nextTotal = baseS * z;

      // World coordinate under pointer should stay fixed.
      const worldX = (point.x - (baseX + currPan.x)) / (currTotal || 1);
      const worldY = (point.y - (baseY + currPan.y)) / (currTotal || 1);

      const nextPan = {
        x: point.x - worldX * nextTotal - baseX,
        y: point.y - worldY * nextTotal - baseY,
      };

      setZoom(z);
      setPan(nextPan);
    },
    [clampZoom, fitBase.scale, fitBase.x, fitBase.y]
  );

  const onWheel = useCallback(
    (e) => {
      e.evt?.preventDefault?.();
      if (!open) return;
      if (!imgObj) return;
      const stage = e.target?.getStage?.();
      const p = stage?.getPointerPosition?.();
      if (!p) return;

      const deltaY = Number(e.evt?.deltaY || 0);
      const direction = deltaY > 0 ? -1 : 1;
      const factor = 1.12;
      const nextZoom = direction > 0 ? (zoomRef.current || 1) * factor : (zoomRef.current || 1) / factor;
      zoomAtCanvasPoint(nextZoom, p);
    },
    [imgObj, open, zoomAtCanvasPoint]
  );

  const onMouseDown = useCallback(
    (e) => {
      if (!imgObj) return;
      const stage = e.target?.getStage?.();
      const p = stage?.getPointerPosition?.();
      if (!p) return;
      isPanningRef.current = true;
      lastPointerRef.current = { x: p.x, y: p.y };
    },
    [imgObj]
  );

  const onMouseMove = useCallback((e) => {
    if (!isPanningRef.current) return;
    const stage = e.target?.getStage?.();
    const p = stage?.getPointerPosition?.();
    if (!p) return;
    const prev = lastPointerRef.current;
    lastPointerRef.current = { x: p.x, y: p.y };
    setPan((cur) => ({ x: cur.x + (p.x - prev.x), y: cur.y + (p.y - prev.y) }));
  }, []);

  const stopPan = useCallback(() => {
    isPanningRef.current = false;
  }, []);

  const onTouchStart = useCallback(
    (e) => {
      e.evt?.preventDefault?.();
      if (!imgObj) return;
      const stage = e.target?.getStage?.();
      const touches = e.evt?.touches || [];
      if (!stage || !touches) return;

      if (touches.length === 1) {
        const t = touches[0];
        touchRef.current = {
          mode: "pan",
          startDist: 0,
          startZoom: zoomRef.current || 1,
          startPan: { ...panRef.current },
          startCenter: { x: t.clientX, y: t.clientY },
        };
      } else if (touches.length >= 2) {
        const t1 = touches[0];
        const t2 = touches[1];
        const dx = t1.clientX - t2.clientX;
        const dy = t1.clientY - t2.clientY;
        const dist = Math.hypot(dx, dy);
        const center = { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
        // Convert screen center to stage pointer space by temporarily setting stage pointer pos.
        const rect = stage.container()?.getBoundingClientRect?.();
        const point = rect ? { x: center.x - rect.left, y: center.y - rect.top } : stage.getPointerPosition?.();
        touchRef.current = {
          mode: "pinch",
          startDist: dist || 1,
          startZoom: zoomRef.current || 1,
          startPan: { ...panRef.current },
          startCenter: point || { x: 0, y: 0 },
        };
      }
    },
    [imgObj]
  );

  const onTouchMove = useCallback(
    (e) => {
      e.evt?.preventDefault?.();
      if (!imgObj) return;
      const stage = e.target?.getStage?.();
      const touches = e.evt?.touches || [];
      if (!stage || !touches) return;

      if (touchRef.current.mode === "pinch" && touches.length >= 2) {
        const t1 = touches[0];
        const t2 = touches[1];
        const dx = t1.clientX - t2.clientX;
        const dy = t1.clientY - t2.clientY;
        const dist = Math.hypot(dx, dy) || 1;
        const ratio = dist / (touchRef.current.startDist || 1);
        const nextZoom = (touchRef.current.startZoom || 1) * ratio;

        const center = { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
        const rect = stage.container()?.getBoundingClientRect?.();
        const point = rect ? { x: center.x - rect.left, y: center.y - rect.top } : touchRef.current.startCenter;
        zoomAtCanvasPoint(nextZoom, point || touchRef.current.startCenter);
        return;
      }

      if (touchRef.current.mode === "pan" && touches.length === 1) {
        const t = touches[0];
        const start = touchRef.current.startCenter;
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        setPan({ x: touchRef.current.startPan.x + dx, y: touchRef.current.startPan.y + dy });
      }
    },
    [imgObj, zoomAtCanvasPoint]
  );

  const onTouchEnd = useCallback(() => {
    touchRef.current = { mode: "none", startDist: 0, startZoom: 1, startPan: { x: 0, y: 0 }, startCenter: { x: 0, y: 0 } };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: fullScreen ? "100vw" : "min(1680px, 96vw)",
          height: fullScreen ? "100vh" : "min(920px, 92vh)",
          m: fullScreen ? 0 : 2,
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ pr: 6, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle1" fontWeight={900} noWrap>
          {title || "Image preview"}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: "100%", overflow: "hidden", display: "grid", gridTemplateRows: "auto minmax(0, 1fr)" }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {onPrev ? (
              <IconButton size="small" color="info" onClick={onPrev} disabled={!canPrev} aria-label="Previous image">
                <Icon>chevron_left</Icon>
              </IconButton>
            ) : null}
            {onNext ? (
              <IconButton size="small" color="info" onClick={onNext} disabled={!canNext} aria-label="Next image">
                <Icon>chevron_right</Icon>
              </IconButton>
            ) : null}
            {positionLabel ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, minWidth: 56 }}>
                {positionLabel}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              Zoom
            </Typography>
            <Box sx={{ width: 220 }}>
              <Slider value={zoom} min={0.15} max={16} step={0.05} onChange={(e, v) => setZoom(clampZoom(Number(v)))} />
            </Box>
            <Button
              size="small"
              variant="outlined"
              color="info"
              onClick={() => {
                setZoom(1);
                setPan({ x: 0, y: 0 });
              }}
              startIcon={<Icon fontSize="small">center_focus_strong</Icon>}
            >
              Reset
            </Button>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {`Wheel/pinch to zoom • Drag to pan • ${anns.length ? `${anns.length} annotation${anns.length === 1 ? "" : "s"}` : "No annotations"}`}
            </Typography>
          </Stack>
        </Box>

        <Box
          ref={canvasRef}
          sx={{ position: "relative", bgcolor: "grey.100", height: "100%", minHeight: { xs: 360, md: 520 }, touchAction: "none", cursor: "grab" }}
        >
          <Stage
            width={canvasSize.w}
            height={canvasSize.h}
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={stopPan}
            onMouseLeave={stopPan}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
          >
            <Layer x={fitBase.x + (pan.x || 0)} y={fitBase.y + (pan.y || 0)} scaleX={totalScale} scaleY={totalScale} listening={false}>
              <KonvaImage image={imgObj} x={0} y={0} width={imgNatural.w} height={imgNatural.h} />
              {anns.map((a, idx) => {
                const w = imgNatural.w || 1;
                const h = imgNatural.h || 1;
                const minDim = Math.min(w, h);
                const x = a.x * w;
                const y = a.y * h;

                if (a.type === "circle") {
                  const rPx = (a.r || 0.04) * minDim;
                  const strokePx = (a.strokeWidth || 0.004) * minDim;
                  return (
                    <Group key={a.id} x={x} y={y}>
                      <Circle x={0} y={0} radius={rPx} stroke={a.color} strokeWidth={strokePx} fill={a.fillOpacity > 0 ? a.color : undefined} opacity={a.opacity} />
                    </Group>
                  );
                }

                if (a.type === "rect") {
                  const boxW = (a.w || 0.10) * w;
                  const boxH = (a.h || 0.10) * h;
                  const strokePx = (a.strokeWidth || 0.004) * minDim;
                  const rx = (a.radius || 0.008) * minDim;
                  return (
                    <Group key={a.id} x={x - boxW / 2} y={y - boxH / 2}>
                      <Rect
                        x={0}
                        y={0}
                        width={boxW}
                        height={boxH}
                        cornerRadius={rx}
                        stroke={a.color}
                        strokeWidth={strokePx}
                        fill={a.fillOpacity > 0 ? a.color : undefined}
                        opacity={a.opacity}
                      />
                    </Group>
                  );
                }

                if (a.type === "path") {
                  const pts = Array.isArray(a.points) ? a.points : [];
                  const pxPts = [];
                  for (let i = 0; i < pts.length; i += 2) {
                    pxPts.push((pts[i] || 0) * w, (pts[i + 1] || 0) * h);
                  }
                  const strokePx = (a.strokeWidth || 0.0035) * minDim;
                  return (
                    <Line
                      key={a.id}
                      points={pxPts}
                      stroke={a.color}
                      strokeWidth={strokePx}
                      tension={typeof a.tension === "number" ? a.tension : 0.4}
                      lineCap={a.lineCap || "round"}
                      lineJoin={a.lineJoin || "round"}
                      opacity={a.opacity}
                    />
                  );
                }

                if (a.type === "text") {
                  const boxW = (a.w || 0.28) * w;
                  const boxH = (a.h || 0.10) * h;
                  const pad = (a.padding || 0.012) * minDim;
                  const fontPx = (a.fontSize || 0.026) * minDim;
                  const rx = (a.radius || 0.012) * minDim;
                  return (
                    <Group key={a.id} x={x - boxW / 2} y={y - boxH / 2}>
                      <Rect width={boxW} height={boxH} cornerRadius={rx} fill={a.color} opacity={a.bgOpacity} />
                      <Text
                        x={pad}
                        y={pad}
                        width={Math.max(10, boxW - pad * 2)}
                        height={Math.max(10, boxH - pad * 2)}
                        text={(a.label || "").trim()}
                        fontFamily={theme.typography.fontFamily}
                        fontStyle="bold"
                        fontSize={fontPx}
                        fill={a.textColor || "#fff"}
                        align="center"
                        verticalAlign="middle"
                        wrap="word"
                        opacity={a.opacity}
                      />
                    </Group>
                  );
                }

                if (a.type === "arrow") {
                  const x1 = (a.x1 || 0) * w;
                  const y1 = (a.y1 || 0) * h;
                  const x2 = (a.x2 || 0) * w;
                  const y2 = (a.y2 || 0) * h;
                  const headPx = (a.headSize || 0.02) * minDim;
                  const strokePx = (a.strokeWidth || 0.004) * minDim;
                  return (
                    <KonvaArrow
                      key={a.id}
                      points={[x1, y1, x2, y2]}
                      stroke={a.color}
                      fill={a.color}
                      strokeWidth={strokePx}
                      pointerLength={headPx}
                      pointerWidth={headPx}
                      opacity={a.opacity}
                    />
                  );
                }

                // note
                const size = a.size || 1;
                const pinR = 0.012 * minDim * size;
                const labelMode = a.labelMode || "number";
                const pinText =
                  labelMode === "none"
                    ? ""
                    : labelMode === "text"
                    ? String((a.label || "").trim()).slice(0, 3)
                    : String(idx + 1);
                return (
                  <Group key={a.id} x={x} y={y}>
                    <Circle x={0} y={0} radius={pinR} fill={a.color} opacity={a.opacity} />
                    {pinText ? (
                      <Text
                        x={-pinR}
                        y={-pinR}
                        width={pinR * 2}
                        height={pinR * 2}
                        text={pinText}
                        fontFamily={theme.typography.fontFamily}
                        fontStyle="bold"
                        fontSize={pinR * 1.35}
                        fill="#fff"
                        align="center"
                        verticalAlign="middle"
                      />
                    ) : null}
                  </Group>
                );
              })}
            </Layer>
          </Stage>

          {!imgObj ? (
            <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", p: 2 }}>
              <Box sx={{ bgcolor: "rgba(255,255,255,0.92)", border: "1px solid", borderColor: "divider", borderRadius: 2, px: 2, py: 1.25 }}>
                <Typography variant="subtitle2" fontWeight={900}>
                  {imgError || "Loading image…"}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: "1px solid", borderColor: "divider", px: 2, py: 1.25 }}>
        <Button onClick={onClose} color="info">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AnnotatedImageViewerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  imageUrl: PropTypes.string,
  annotations: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  canPrev: PropTypes.bool,
  canNext: PropTypes.bool,
  positionLabel: PropTypes.string,
};

